import React, { useEffect, useState } from 'react';
import { Header, Toast, Button, Groupper, Input, Table, Pagination, Icon, Label, Modal } from 'react-frontier';
import { Dropdown } from 'semantic-ui-react';
import classNames from 'classnames';
import moment from 'moment';
import { Link } from 'react-router-dom';
import API from '../API';
import UserAccess from '../UserAccess';
import { useBreadcrumbs, useUser } from '../hooks';
import { Notification } from '../components/Notifications';
import { SetLoading } from '../SiteClasses';
import { bindClick } from '../SiteUtil';

const PAGE_SIZE = 20;

enum NotificationTypeFilter{
	REQUEST = 1,
	SHIPMENT = 2,
	EVENT = 3,
}

var getNotificationType = (notification: Notification)=>{
	if(notification.url?.startsWith('/requests/')) return NotificationTypeFilter.REQUEST;
	if(notification.url?.startsWith('/shipments/')) return NotificationTypeFilter.SHIPMENT;
	if(notification.date_id) return NotificationTypeFilter.EVENT;
	return null;
}

var getNotificationTypeLabel = (notification: Notification)=>{
	var type = getNotificationType(notification);
	if(type === NotificationTypeFilter.REQUEST) return 'Solicitud';
	if(type === NotificationTypeFilter.SHIPMENT) return 'Envío';
	if(type === NotificationTypeFilter.EVENT) return 'Evento';
	return 'General';
}

var UserNotifications = ()=>{
	var { setTitle } = useBreadcrumbs();
	var { updateNotifications, user, hasAccess } = useUser();
	var [loadError, setLoadError] = useState<string>(null);
	var [loading, setLoading] = useState<boolean>(false);
	var [notifications, setNotifications] = useState<Notification[]>(null);
	var [input, setInput] = useState<string>('');
	var [onlyUnread, setOnlyUnread] = useState<boolean>(false);
	var [typeFilter, setTypeFilter] = useState<NotificationTypeFilter>(null);
	var [page, setPage] = useState<number>(0);
	var [hasNext, setHasNext] = useState<boolean>(false);
	var [searchTimeout, setSearchTimeout] = useState<any>(null);
	var [showDismissAllModal, setShowDismissAllModal] = useState<boolean>(false);

	useEffect(()=>{
		setTitle('Mis Notificaciones');
		get(0);
	}, []);

	var get = (getPage: number = 0, query: string = input, currentOnlyUnread: boolean = onlyUnread, currentTypeFilter: NotificationTypeFilter = typeFilter)=>{
		var offset = getPage > 0 ? getPage * PAGE_SIZE : 0;
		setLoading(true);
		setLoadError(null);

		API.getNotificationsInbox(offset, PAGE_SIZE, query, currentOnlyUnread, currentTypeFilter).then(res=>{
			if(res.error) return setLoadError(res.message);
			setNotifications(res.data);
			setPage(getPage);
			setHasNext(res.data.length >= PAGE_SIZE);
		}).catch(err=>{
			setLoadError('Hubo un error al obtener tus notificaciones. (UNTF-1)');
		}).finally(()=>{
			setLoading(false);
		});
	}

	var onPageChange = (nextPage: number)=>{
		setPage(nextPage);
		get(nextPage);
	}

	var onSearchChange = (val: string)=>{
		setInput(val);
		if(searchTimeout) clearTimeout(searchTimeout);
		setSearchTimeout(setTimeout(()=>{
			setPage(0);
			get(0, val, onlyUnread, typeFilter);
		}, 500));
	}

	var onFilterChange = (nextOnlyUnread: boolean = false, nextTypeFilter: NotificationTypeFilter = null)=>{
		if(searchTimeout){
			clearTimeout(searchTimeout);
			setSearchTimeout(null);
		}
		setOnlyUnread(nextOnlyUnread);
		setTypeFilter(nextTypeFilter);
		setPage(0);
		get(0, input, nextOnlyUnread, nextTypeFilter);
	}

	var onOpenNotification = (notification: Notification)=>{
		if(notification.dismissed) return;

		API.dismissNotification(notification.notification_id).then(res=>{
			if(res.error) return Toast.error(res.message);
			setNotifications(prev => onlyUnread
				? prev.filter(a => a.notification_id !== notification.notification_id)
				: prev.map(a => a.notification_id === notification.notification_id ? { ...a, dismissed: true } : a)
			);
			if(user?.notifications > 0){
				updateNotifications(user.notifications - 1);
			}
		}).catch(err=>{
			Toast.error('Error al marcar la notificación como leída (LCL-1)');
		});
	}

	var onDismissAll = (setLoading: SetLoading)=>{
		setLoading(true);
		API.dismissAllNotifications().then(res=>{
			if(res.error) return Toast.error(res.message);
			Toast.success('Notificaciones marcadas como leídas');
			setNotifications(prev => onlyUnread ? [] : prev.map(a => ({ ...a, dismissed: true })));
			updateNotifications(0);
			setShowDismissAllModal(false);
		}).catch(err=>{
			Toast.error('Error al marcar las notificaciones como leídas (LCL-2)');
		}).finally(()=>{
			setLoading(false);
		});
	}

	if(loadError) return <Header text='Error' subtext={loadError} />;
	if(!notifications) return <Header text='Cargando datos' loading />;

	return <div>
		<Groupper
			title='Mis Notificaciones'
			style={{ marginTop: 20 }}
			width={900}
		>
			<div style={{ display: 'flex', gap: 10, marginBottom: 15, alignItems: 'center' }}>
				<Input
					placeholder='Buscar por ID, título o mensaje...'
					value={input}
					onChange={onSearchChange}
					style={{ flex: 1 }}
				/>
				<Dropdown icon={null} pointing direction='left' trigger={(
					<Button
						text='Filtrar'
						size='small'
						iconName='filter'
					/>
					)}>
						<Dropdown.Menu>
							{[
								{ id: 'all', label: 'Todas', onlyUnread: false, typeFilter: null },
								{ id: 'unread', label: 'No leídas', onlyUnread: true, typeFilter: null },
								...(hasAccess(UserAccess.INVENTORY.VIEW_INVENTORY_REQUESTS) ? [{ id: 'request', label: 'Solicitudes', onlyUnread: false, typeFilter: NotificationTypeFilter.REQUEST }] : []),
								...(hasAccess(UserAccess.INVENTORY.VIEW_INVENTORY_SHIPMENTS) ? [{ id: 'shipment', label: 'Envíos', onlyUnread: false, typeFilter: NotificationTypeFilter.SHIPMENT }] : []),
								...(hasAccess(UserAccess.EVENTS.VIEW_EVENTS) ? [{ id: 'event', label: 'Eventos', onlyUnread: false, typeFilter: NotificationTypeFilter.EVENT }] : []),
							].map(option => (
								<Dropdown.Item key={option.id} onClick={() => onFilterChange(option.onlyUnread, option.typeFilter)}>
									<i className={classNames('icon circle', {
										'check': onlyUnread === option.onlyUnread && typeFilter === option.typeFilter,
									'outline': onlyUnread !== option.onlyUnread || typeFilter !== option.typeFilter,
								})}></i>
								{option.label}
							</Dropdown.Item>
						))}
					</Dropdown.Menu>
				</Dropdown>
				<Button
					text='Marcar todas leídas'
					size='small'
					iconName='check-circle'
					disabled={!user?.notifications}
					style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
					onClick={bindClick(setShowDismissAllModal, true)}
				/>
			</div>
			<Table
				striped
				className='first last'
				headers={['ID', 'Notificación', 'Tipo', 'Fecha', 'Estatus', <Icon name='wrench' />]}
				collapsingIndexes={[2, 3, 4, 5]}
				centeredIndexes={[4, 5]}
				footer={(hasNext || page !== 0) && (
					<Table.Cell colSpan={6} row>
						<div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
							<Pagination page={page} onPageChange={onPageChange} hasNext={hasNext} />
						</div>
					</Table.Cell>
				)}
			>
				{loading ? (
					<Table.Cell row className='empty' colSpan={6}>
						<Header loading text='Cargando datos' />
					</Table.Cell>
				) : notifications.length === 0 ? (
					<Table.Cell row className='empty' colSpan={6}>
						No se encontraron registros
					</Table.Cell>
				) : (
					notifications.map(notification => (
						<Table.Row
							key={notification.notification_id}
							collapsingIndexes={[0, 2, 3, 5]}
							data={[
								notification.notification_id,
								<div style={{ whiteSpace: 'normal' }}>
									<div style={{ fontWeight: 600 }}>{notification.title}</div>
									<div style={{ color: '#606060', marginTop: 2 }}>{notification.message}</div>
								</div>,
								getNotificationTypeLabel(notification),
								moment.unix(notification.date_created).format('DD/MMM/YY HH:mm'),
								<Label style={{ padding: '1px 10px', fontSize: 12, minWidth: 95 }} color={notification.dismissed ? 'green' : 'orange'}>
									<Icon name={notification.dismissed ? 'check-circle' : 'circle'} />
									{notification.dismissed ? 'Leída' : 'No leída'}
								</Label>,
								<Dropdown icon={(
									<div className="fr label" style={{ padding: '3px 10px' }}>
										<Icon name='ellipsis' style={{ margin: 0 }} />
									</div>
								)} className='icon'>
									<Dropdown.Menu>
										<Dropdown.Item as={Link} to={notification.url} onClick={() => onOpenNotification(notification)}>
											<Icon name="eye" style={{ marginRight: 3 }} /> Ver
										</Dropdown.Item>
										{!notification.dismissed && (
											<Dropdown.Item onClick={() => onOpenNotification(notification)}>
												<Icon name="check" style={{ marginRight: 3 }} /> Marcar leida
											</Dropdown.Item>
										)}
									</Dropdown.Menu>
								</Dropdown>
							]}
						/>
					))
				)}
			</Table>
		</Groupper>
		<Modal open={showDismissAllModal} onClose={bindClick(setShowDismissAllModal, false)} size='mini'>
			<Modal.Header>Marcar todas como leídas</Modal.Header>
			<Modal.Content>
				<Header text='¿Marcar todas tus notificaciones como leídas?' />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic onClick={bindClick(setShowDismissAllModal, false)} />
				<Button text='Marcar leídas' color='orange' onClick={onDismissAll} />
			</Modal.Actions>
		</Modal>
	</div>
}

export default UserNotifications;
